import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  Box,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import { mockSegments, segmentTypes } from '../data/mockSegments';

const CustomerSegments = () => {
  const navigate = useNavigate();
  const [segments, setSegments] = useState(mockSegments);
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedSegment, setSelectedSegment] = useState(null);

  const handleMenuOpen = (event, segment) => {
    setAnchorEl(event.currentTarget);
    setSelectedSegment(segment);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedSegment(null);
  };

  const getSegmentTypeLabel = (type) => {
    const segmentType = segmentTypes.find(t => t.value === type);
    return segmentType ? segmentType.label : type;
  };

  const formatNumber = (num) => {
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'k';
    }
    return num.toString();
  };

  const filteredSegments = segments.filter(segment =>
    segment.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    segment.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDeleteSegment = (segmentId) => {
    setSegments(segments.filter(segment => segment.id !== segmentId));
    handleMenuClose();
  };

  return (
    <Card>
      <CardHeader
        title={
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" fontWeight={600}>
              Customer Segments
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/marketing/segments/create')}
              sx={{
                textTransform: 'none',
                backgroundColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(23, 49, 49, 0.3)'
                }
              }}
            >
              Create Segment
            </Button>
          </Box>
        }
        sx={{ pb: 2 }}
      />
      <CardContent>
        <Box sx={{ mb: 3 }}>
          <TextField
            placeholder="Search segments..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 300 }}
          />
        </Box>

        <Grid container spacing={3}>
          {filteredSegments.map((segment) => (
            <Grid item xs={12} md={6} lg={4} key={segment.id}>
              <Card
                sx={{
                  height: '100%',
                  border: `2px solid ${segment.color}20`,
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s ease'
                  }
                }}
              >
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box>
                      <Typography variant="h6" fontWeight={600} gutterBottom>
                        {segment.name}
                      </Typography>
                      <Chip
                        label={getSegmentTypeLabel(segment.type)}
                        size="small"
                        sx={{
                          backgroundColor: `${segment.color}20`,
                          color: segment.color,
                          fontWeight: 500
                        }}
                      />
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, segment)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {segment.description}
                  </Typography>

                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <PeopleIcon fontSize="small" color="action" />
                    <Typography variant="body2" fontWeight={500}>
                      {formatNumber(segment.estimatedSize)} customers
                    </Typography>
                  </Box>

                  <Typography variant="caption" color="text.secondary">
                    Created {new Date(segment.createdAt).toLocaleDateString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {filteredSegments.length === 0 && (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary">
              No segments found matching your search.
            </Typography>
          </Box>
        )}

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => {
            navigate(`/marketing/segments/${selectedSegment?.id}/edit`);
            handleMenuClose();
          }}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edit Segment
          </MenuItem>
          <MenuItem
            onClick={() => handleDeleteSegment(selectedSegment?.id)}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete Segment
          </MenuItem>
        </Menu>
      </CardContent>
    </Card>
  );
};

export default CustomerSegments;